import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useLexicalEditable } from '@lexical/react/useLexicalEditable'
import {
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  getDOMCellFromTarget,
} from '@lexical/table'
import { calculateZoomLevel } from '@lexical/utils'
import type { LexicalEditor } from 'lexical'
import {
  $getNearestNodeFromDOMNode,
  $getSelection,
  COMMAND_PRIORITY_HIGH,
  SELECTION_CHANGE_COMMAND
} from 'lexical'
import * as React from 'react'
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'

const MIN_ROW_HEIGHT = 33
const MIN_COLUMN_WIDTH = 50

type MousePos = {
  x: number;
  y: number;
};

function TableCellResizerInner({ editor }: { editor: LexicalEditor }): React.JSX.Element {
  const targetRef = useRef<HTMLElement | null>(null)
  const resizerRef = useRef<HTMLDivElement | null>(null)
  const tableRectRef = useRef<DOMRect | null>(null)

  const mouseStartPosRef = useRef<MousePos | null>(null)
  const [mouseCurrentPos, updateMouseCurrentPos] = useState<MousePos | null>(null)

  const [activeCell, updateActiveCell] = useState<any | null>(null)
  const [isSelectingGrid, updateIsSelectingGrid] = useState(false)
  const [draggingDirection, updateDraggingDirection] = useState<'right' | 'bottom' | null>(null)

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection()
        const isTableSelectionVal = $isTableSelection(selection)

        if (isSelectingGrid !== isTableSelectionVal) {
          updateIsSelectingGrid(isTableSelectionVal)
        }

        return false
      },
      COMMAND_PRIORITY_HIGH
    )
  })

  const resetState = useCallback(() => {
    updateActiveCell(null)
    targetRef.current = null
    updateDraggingDirection(null)
    mouseStartPosRef.current = null
    tableRectRef.current = null
  }, [])

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      setTimeout(() => {
        const target = event.target as HTMLElement

        if (draggingDirection) {
          updateMouseCurrentPos({
            x: event.clientX,
            y: event.clientY
          })
          return
        }

        if (resizerRef.current && resizerRef.current.contains(target)) {
          return
        }

        if (targetRef.current !== target) {
          targetRef.current = target
          const cell = getDOMCellFromTarget(target)

          if (cell && activeCell !== cell) {
            editor.update(() => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem)
              if (!tableCellNode) {
                throw new Error('TableCellResizerInner: Table cell node not found.')
              }

              const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)
              const tableElement = editor.getElementByKey(tableNode.getKey())

              if (!tableElement) {
                throw new Error('TableCellResizerInner: Table element not found.')
              }

              targetRef.current = target
              tableRectRef.current = tableElement.getBoundingClientRect()
              updateActiveCell(cell)
            })
          } else if (cell == null) {
            resetState()
          }
        }
      }, 0)
    }

    document.addEventListener('mousemove', onMouseMove)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
    }
  }, [activeCell, draggingDirection, editor, resetState])

  const isHeightChanging = (direction: string | null) => {
    if (direction === 'bottom') {
      return true
    }
    return false
  }

  const updateRowHeight = useCallback(
    (newHeight: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizerInner: Expected active cell.')
      }

      editor.update(() => {
        const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem)
        if (!$isTableCellNode(tableCellNode)) {
          throw new Error('TableCellResizerInner: Table cell node not found.')
        }

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)

        const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode)

        const tableRows = tableNode.getChildren()

        if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
          throw new Error('Expected table cell to be inside of table row.')
        }

        const tableRow = tableRows[tableRowIndex]

        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row')
        }

        // Handle possible Lexical API variations for TableRowNode height
        if ((tableRow as any).setHeight) {
          (tableRow as any).setHeight(newHeight)
        } else if ((tableRow as any).setRowHeight) {
          (tableRow as any).setRowHeight(newHeight)
        }
      })
    },
    [activeCell, editor]
  )

  const updateColumnWidth = useCallback(
    (newWidth: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizerInner: Expected active cell.')
      }
      editor.update(() => {
        const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem)
        if (!$isTableCellNode(tableCellNode)) {
          throw new Error('TableCellResizerInner: Table cell node not found.')
        }

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode)

        const tableColumnIndex = $getTableColumnIndexFromTableCellNode(tableCellNode)

        const tableRows = tableNode.getChildren()

        for (let r = 0; r < tableRows.length; r++) {
          const tableRow = tableRows[r]

          if (!$isTableRowNode(tableRow)) {
            throw new Error('Expected table row')
          }

          const rowCells = tableRow.getChildren()
          const rowCellsSpan = rowCells.map((cell) => $isTableCellNode(cell) ? cell.getColSpan() : 1)

          const aggregatedRowSpans = (rowCellsSpan as number[]).reduce((rowSpans, cellSpan) => {
            const previousCell = rowSpans[rowSpans.length - 1] ?? 0
            rowSpans.push(previousCell + cellSpan)
            return rowSpans
          }, [] as number[])
          const rowColumnIndexWithSpan = aggregatedRowSpans.findIndex(
            (cellSpan) => cellSpan > tableColumnIndex
          )

          if (rowColumnIndexWithSpan >= rowCells.length || rowColumnIndexWithSpan < 0) {
            throw new Error('Expected table cell to be inside of table row.')
          }

          const tableCell = rowCells[rowColumnIndexWithSpan]

          if (!$isTableCellNode(tableCell)) {
            throw new Error('Expected table cell')
          }

          // Handle possible Lexical API variations for TableCellNode width
          if ((tableCell as any).setWidth) {
            (tableCell as any).setWidth(newWidth)
          } else if ((tableCell as any).setColWidth) {
            (tableCell as any).setColWidth(newWidth)
          }
        }
      })
    },
    [activeCell, editor]
  )

  const mouseUpHandler = useCallback(
    (direction: 'right' | 'bottom') => {
      const handler = (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()

        if (!activeCell) {
          throw new Error('TableCellResizerInner: Expected active cell.')
        }

        if (mouseStartPosRef.current) {
          const { x, y } = mouseStartPosRef.current

          if (activeCell === null) {
            return
          }
          const zoom = calculateZoomLevel(event.target as HTMLElement)

          if (isHeightChanging(direction)) {
            const height = activeCell.elem.getBoundingClientRect().height
            const heightChange = Math.abs(event.clientY - y) / zoom

            const isShrinking = direction === 'bottom' && y > event.clientY

            updateRowHeight(
              Math.max(isShrinking ? height - heightChange : heightChange + height, MIN_ROW_HEIGHT)
            )
          } else {
            const computedStyle = getComputedStyle(activeCell.elem)
            let width = activeCell.elem.clientWidth // width with padding
            width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)
            const widthChange = Math.abs(event.clientX - x) / zoom

            const isShrinking = direction === 'right' && x > event.clientX

            updateColumnWidth(
              Math.max(isShrinking ? width - widthChange : widthChange + width, MIN_COLUMN_WIDTH)
            )
          }

          resetState()
          document.removeEventListener('mouseup', handler)
        }
      }
      return handler
    },
    [activeCell, resetState, updateColumnWidth, updateRowHeight]
  )

  const toggleResize = useCallback(
    (direction: 'right' | 'bottom') => (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (!activeCell) {
        throw new Error('TableCellResizerInner: Expected active cell.')
      }

      mouseStartPosRef.current = {
        x: event.clientX,
        y: event.clientY
      }
      updateMouseCurrentPos(mouseStartPosRef.current)
      updateDraggingDirection(direction)

      document.addEventListener('mouseup', mouseUpHandler(direction))
    },
    [activeCell, mouseUpHandler]
  )

  const getResizers = useCallback(() => {
    if (activeCell) {
      const { height, width, top, left } = activeCell.elem.getBoundingClientRect()
      const zoom = calculateZoomLevel(activeCell.elem)

      const styles: Record<string, React.CSSProperties | null> = {
        bottom: {
          backgroundColor: 'none',
          cursor: 'row-resize',
          height: '10px',
          left: `${window.scrollX + left}px`,
          top: `${window.scrollY + top + height}px`,
          width: `${width}px`,
          position: 'absolute'
        },
        right: {
          backgroundColor: 'none',
          cursor: 'col-resize',
          height: `${height}px`,
          left: `${window.scrollX + left + width}px`,
          top: `${window.scrollY + top}px`,
          width: '10px',
          position: 'absolute'
        }
      }

      const tableRect = tableRectRef.current

      if (draggingDirection && mouseCurrentPos && tableRect) {
        const currentStyle = styles[draggingDirection];
        if (currentStyle) {
          if (isHeightChanging(draggingDirection)) {
            currentStyle.left = `${window.scrollX + tableRect.left}px`
            currentStyle.top = `${window.scrollY + mouseCurrentPos.y / zoom}px`
            currentStyle.height = '3px'
            currentStyle.width = `${tableRect.width}px`
          } else {
            currentStyle.top = `${window.scrollY + tableRect.top}px`
            currentStyle.left = `${window.scrollX + mouseCurrentPos.x / zoom}px`
            currentStyle.width = '3px'
            currentStyle.height = `${tableRect.height}px`
          }
          currentStyle.backgroundColor = '#adf'
        }
      }

      return styles
    }

    return {
      bottom: null,
      left: null,
      right: null,
      top: null
    }
  }, [activeCell, draggingDirection, mouseCurrentPos])

  const resizerStyles = getResizers()

  return (
    <div ref={resizerRef}>
      {activeCell != null && !isSelectingGrid && (
        <>
          <div
            className='table-cell-resizer__resizer table-cell-resizer__ui'
            style={resizerStyles.right || undefined}
            onMouseDown={toggleResize('right')}
          />
          <div
            className='table-cell-resizer__resizer table-cell-resizer__ui'
            style={resizerStyles.bottom || undefined}
            onMouseDown={toggleResize('bottom')}
          />
        </>
      )}
    </div>
  )
}

export default function TableCellResizerPlugin(): React.ReactPortal | null {
  const [editor] = useLexicalComposerContext()
  const isEditable = useLexicalEditable()

  return isEditable ? createPortal(<TableCellResizerInner editor={editor} />, document.body) : null
}
